<template>
  <div id="vouchers">
    <h3>Vouchers</h3>
    <div v-if="organizations.length >= 1" class="form-group">
      <OrganizationSelect v-bind="$attrs" :organizations="organizations" :organization-id="organizationId" />
    </div>
    <div id="voucher_form" class="form-group vouchers">
      <h4>
        <span>{{ editId != null ? "Edit" : "Add" }} Voucher</span>
        <span v-if="editId != null"> &middot; <a @click="reset">Cancel</a></span>
      </h4>
      <p class="infomsg">Adding a new voucher will immediately send an email notification to the traveler. Please verify all information is correct before saving.</p>
      <div class="form" :class="{ loading: formIsLoading }">
        <Result
          v-model:error="formError"
          v-model:is-success="formIsSuccess"
          @close="
            formError = null;
            formIsSuccess = false;
          "
        />
        <div class="form-section">
          <div class="form-row">
            <input v-model="formCustomerName" type="text" maxlength="255" required placeholder="Customer Name" />
          </div>
          <div class="form-row">
            <input v-model="formCustomerEmail" type="email" maxlength="255" required placeholder="Customer Email" :disabled="editId != null" />
          </div>
          <div class="form-row">
            <input v-model="formAmountUsd" type="number" step="0.01" min="0" required placeholder="Amount in USD" />
          </div>
          <div class="form-row">
            <DatePicker v-model:date="formExpired" placeholder="Expiry Date" />
          </div>
        </div>
        <button @click="save">Save Voucher</button>
      </div>
    </div>
    <div v-if="tableVouchers.length > 0" class="form-group">
      <h4>Voucher List</h4>
      <p class="infomsg">Vouchers allow you to pay for a portion of a traveler's trip. Create a voucher to assign a specific amount to a single traveler.</p>
      <div id="filter">
        <div class="filter-row">
          <select v-model="statusFilter" class="other">
            <option value="available">Available</option>
            <option value="unavailable">Unavailable</option>
          </select>
          <div class="search">
            <span class="icon">&#x1f50e;</span>
            <input v-model="searchQuery" type="text" placeholder="Search" />
          </div>
        </div>
      </div>
      <table :class="{ loading: tableIsLoading }">
        <thead>
          <tr>
            <th>Name</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Created</th>
            <th>Expiry</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="sortedVouchers.length == 0">
            <td colspan="6">No results</td>
          </tr>
          <tr v-for="v in sortedVouchers" v-else :key="v.id">
            <td>{{ v.customer_name || "-" }}</td>
            <td>{{ formatCurrency(v.amount_usd) }}</td>
            <td>
              <span
                :class="{
                  red: v.used || !v.is_active,
                  green: !v.used && v.is_active,
                }"
              >
                {{ getStatusText(v) }}
              </span>
            </td>
            <td>{{ formatDate(v.created) }}</td>
            <td>{{ v.expired ? formatDate(v.expired) : "-" }}</td>
            <td class="actions">
              <template v-if="!v.used">
                <a v-if="v.is_active" @click="actionEdit(v.id)">Edit</a>
                <a @click="actionToggleActive(v.id, v.is_active)">
                  {{ v.is_active ? "Disable" : "Enable" }}
                </a>
              </template>
              <span v-else-if="v.trip_id">
                <router-link :to="{ name: 'trip', params: { tripId: v.trip_id } }" target="_blank"> View trip </router-link>
              </span>
              <span v-else>-</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from "vue";
import Result from "../components/common/FormResult.vue";
import OrganizationSelect from "../components/common/OrganizationSelect.vue";
import DatePicker from "../components/common/PickerDate.vue";
import useApi from "../utils/apiCalls.js";

const props = defineProps(["organizations", "organizationId"]);
const emit = defineEmits(["error", "info", "scroll"]);

// Reactive State
const editId = ref(null);
const formCustomerName = ref("");
const formCustomerEmail = ref("");
const formAmountUsd = ref("");
const formExpired = ref("");
const formIsLoading = ref(false);
const formIsSuccess = ref(false);
const formError = ref(null);
const tableIsLoading = ref(true);
const tableVouchers = ref([]);

// Search and filter state
const searchQuery = ref("");
const statusFilter = ref("available");

// API Utilities
const { apiGetRequest, apiPutRequest, apiPostRequest } = useApi(emit);

// Watchers
watch(editId, newValue => {
  if (newValue != null) {
    const el = document.getElementById("voucher_form");
    el.scrollIntoView({ behavior: "smooth" });
    loadVoucher();
  }
});

watch(
  () => props.organizationId,
  () => {
    load();
  }
);

// Lifecycle Hooks
onMounted(() => {
  if (props.organizationId != null) {
    load();
  }
});

// Utility methods
const formatCurrency = amount => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const formatDate = dateString => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
};

// Computed property for filtered vouchers
const sortedVouchers = computed(() => {
  let filteredVouchers = tableVouchers.value;

  // Apply status filter
  if (statusFilter.value === "available") {
    filteredVouchers = filteredVouchers.filter(voucher => !voucher.used && voucher.is_active);
  } else if (statusFilter.value === "unavailable") {
    filteredVouchers = filteredVouchers.filter(voucher => voucher.used || !voucher.is_active);
  }

  // Apply search filter if there is a search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filteredVouchers = filteredVouchers.filter(voucher => {
      return (voucher.customer_name && voucher.customer_name.toLowerCase().includes(query)) || voucher.customer_email.toLowerCase().includes(query) || voucher.id.toString().includes(query) || voucher.amount_usd.toString().includes(query);
    });
  }

  return filteredVouchers;
});

// CRUD Methods
const getStatusText = voucher => {
  if (voucher.used) {
    return `Used on ${formatDate(voucher.used)}`;
  }
  if (!voucher.is_active) {
    return "Disabled";
  }
  return "Available";
};

const actionToggleActive = (id, currentStatus) => {
  const action = currentStatus ? "deactivate" : "activate";
  if (currentStatus && !confirm(`Are you sure you want to ${action} this voucher?`)) {
    return;
  }

  apiPutRequest({
    path: `/vouchers/${id}/set-active?organization_id=${props.organizationId}&action=${action}`,
    onSuccess: () => {
      emit("info", `Voucher ${action}d successfully`);
      load();
    },
    onError: error => {
      formError.value = error;
    },
  });
};

const actionEdit = id => {
  formIsSuccess.value = false;
  formError.value = null;
  editId.value = id;
};

const load = () => {
  tableIsLoading.value = true;

  apiGetRequest({
    path: `/vouchers?organization_id=${props.organizationId}`,
    onSuccess: response => {
      tableVouchers.value = response;
      tableIsLoading.value = false;
    },
    onError: error => {
      tableIsLoading.value = false;
      emit("error", error);
    },
  });
};

const loadVoucher = () => {
  formIsLoading.value = true;

  apiGetRequest({
    path: `/vouchers/${editId.value}?organization_id=${props.organizationId}`,
    onSuccess: response => {
      formCustomerName.value = response.customer_name || "";
      formCustomerEmail.value = response.customer_email;
      formAmountUsd.value = response.amount_usd;
      formExpired.value = response.expired ? new Date(response.expired) : "";
      formIsLoading.value = false;
    },
    onError: error => {
      formIsLoading.value = false;
      emit("error", error);
    },
  });
};

const reset = () => {
  editId.value = null;
  formCustomerName.value = "";
  formCustomerEmail.value = "";
  formAmountUsd.value = "";
  formExpired.value = "";
};

const save = () => {
  formIsLoading.value = true;

  const data = {
    customer_name: formCustomerName.value.trim(),
    customer_email: formCustomerEmail.value,
    amount_usd: Number(formAmountUsd.value),
    organization_id: Number(props.organizationId),
    expired: formExpired.value ? formExpired.value.toISOString() : null,
  };

  const url = editId.value != null ? `/vouchers/${editId.value}?organization_id=${props.organizationId}` : `/vouchers?organization_id=${props.organizationId}`;
  const method = editId.value != null ? apiPutRequest : apiPostRequest;

  method({
    path: url,
    data: data,
    onSuccess: () => {
      reset();
      formIsSuccess.value = true;
      formError.value = null;
      formIsLoading.value = false;
      load();
    },
    onError: error => {
      formIsSuccess.value = false;
      formError.value = error;
      formIsLoading.value = false;
      console.log(error);
    },
  });
};
</script>

<style lang="less">
#vouchers {
  .date-picker {
    width: 100%;

    .icon {
      display: none;
    }

    input {
      text-indent: 0;
    }
  }
}
</style>
